import { t } from '@/utils/i18n'
import { Container } from '@/components/ui/Container'
import { Animations } from '@/utils/styles'
import { Card } from '@/components/ui/Card'

export function ValuePropositionSection(): HTMLElement {
  const section = document.createElement('section')
  section.className = 'section-container relative bg-slate-50 overflow-hidden'

  // Background decoration
  const bgDeco = document.createElement('div')
  bgDeco.className = 'absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none'
  bgDeco.innerHTML = `
    <div class="absolute -top-24 -right-24 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl"></div>
    <div class="absolute top-1/2 -left-24 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl"></div>
  `
  section.appendChild(bgDeco)

  const container = Container({
    children: [
      createSectionHeader(),
      createImageSection(),
      createUSPGrid(),
    ],
    className: 'relative z-10',
  })

  section.appendChild(container)
  return section
}

function createSectionHeader(): HTMLElement {
  const header = document.createElement('div')
  header.className = 'text-center mb-20'

  const title = document.createElement('h2')
  title.textContent = t('valueProposition.sectionTitle')
  title.className = 'text-4xl sm:text-5xl font-bold text-slate-900 mb-6 tracking-tight'

  const subtitle = document.createElement('p')
  subtitle.textContent = t('valueProposition.sectionSubtitle')
  subtitle.className = 'text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed'

  header.appendChild(title)
  header.appendChild(subtitle)
  return header
}

function createUSPGrid(): HTMLElement {
  const grid = document.createElement('div')
  grid.className = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'

  const cards = [
    {
      iconPath: 'M15 13V5c0-1.66-1.34-3-3-3S9 3.34 9 5v8c-1.21.91-2 2.37-2 4 0 2.76 2.24 5 5 5s5-2.24 5-5c0-1.63-.79-3.09-2-4zm-4-8c0-.55.45-1 1-1s1 .45 1 1h-1v1h1v2h-1v1h1v2h-2V5z',
      title: t('valueProposition.card1.title'),
      description: t('valueProposition.card1.description'),
    },
    {
      iconPath: 'M3 3h18v4H3V3zm0 6h18v10c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V9zm6 8h6v2H9v-2z',
      title: t('valueProposition.card2.title'),
      description: t('valueProposition.card2.description'),
    },
    {
      iconPath: 'M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z',
      title: t('valueProposition.card3.title'),
      description: t('valueProposition.card3.description'),
    },
    {
      iconPath: 'M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4zM11 20v-5.5H9L13 7v5.5h2L11 20z',
      title: t('valueProposition.card4.title'),
      description: t('valueProposition.card4.description'),
    },
  ]

  cards.forEach((cardData, index) => {
    const cardContent = document.createElement('div')
    cardContent.className = 'text-center h-full flex flex-col items-center'

    // Icon container with gradient
    const iconContainer = document.createElement('div')
    iconContainer.className = 'w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 text-orange-600 p-4 flex items-center justify-center shadow-sm transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3'

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'currentColor')
    svg.setAttribute('class', 'w-full h-full')

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', cardData.iconPath)

    svg.appendChild(path)
    iconContainer.appendChild(svg)

    const title = document.createElement('h3')
    title.textContent = cardData.title
    title.className = 'text-xl font-bold text-slate-900 mb-3'

    const description = document.createElement('p')
    description.textContent = cardData.description
    description.className = 'text-slate-600 text-sm leading-relaxed'

    cardContent.appendChild(iconContainer)
    cardContent.appendChild(title)
    cardContent.appendChild(description)

    const card = Card({
      children: cardContent,
      className: 'group h-full animate-fade-in-up',
      hover: true,
      padding: 'lg',
    })
    card.style.animationDelay = Animations.staggerDelay(index)

    grid.appendChild(card)
  })

  return grid
}

function createImageSection(): HTMLElement {
  const imageContainer = document.createElement('div')
  imageContainer.className = 'my-12 md:my-16 lg:my-24 flex justify-center px-6 md:px-8 lg:px-12 relative'

  // Glow effect behind image
  const glow = document.createElement('div')
  glow.className = 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-orange-500/10 blur-3xl rounded-full -z-10'
  imageContainer.appendChild(glow)

  // Bounding box wrapper with padding (similar to Technology section video container)
  const imageWrapper = document.createElement('div')
  imageWrapper.className = 'relative max-w-5xl w-full glass rounded-3xl pt-16 pb-4 px-4 shadow-lg'

  const image = document.createElement('img')
  image.src = '/images/IsoTES_outer.webp'
  image.alt = 'IsoTES® Technology'
  image.className = 'w-full h-auto rounded-2xl transform hover:scale-[1.01] transition-transform duration-500'

  imageWrapper.appendChild(image)
  imageContainer.appendChild(imageWrapper)

  return imageContainer
}
